import { get, post } from './API';

/**
 * A booking's order_lines ledger: the read, and the writes against it.
 *
 * All four writes go to the ONE existing `POST /bookings/order-lines` route,
 * discriminated by `action` — no new API resource, and therefore no new IAM
 * statement to deploy before these can ship.
 *
 * They exist because the ledger was append-only. The screen's only write was
 * `adjustments.push(...)` on a whole-booking PUT, so fixing a typo meant
 * adding a second line with a minus in front of it, and the second line was
 * indistinguishable from a real credit. The server decides what is allowed:
 * a line a Payment or an Invoice references is refused for edit and delete
 * and must be reversed instead.
 */

/** The four adjustment fields every write shares, in the API's naming. */
function adjustmentPayload(input) {
  return {
    description: input.description,
    customer_amount: input.customerAmount,
    operator_amount: input.operatorAmount,
    operator_id: input.operatorId,
    role: input.role,
    currency: input.currency,
  };
}

export default {
  /**
   * The booking's ledger: `{ lines (with running balances and FX-converted
   * amounts), balance, balance_by_operator, locks }`, or undefined when the
   * booking is gone.
   */
  get: async (bookingId) => {
    const response = await get('bookings/order-lines', { _id: bookingId });
    return response?.data?.data;
  },

  /** Append a new adjustment. Refused if an identical line already exists. */
  addAdjustment: async (bookingId, input) => post('bookings/order-lines', {
    action: 'add_adjustment',
    booking_id: bookingId,
    ...adjustmentPayload(input),
  }),

  /**
   * Change an adjustment in place. Refused (409) when a payment or invoice
   * already points at the line — those get `reverseAdjustment`.
   */
  updateAdjustment: async (bookingId, adjustmentId, input) => post('bookings/order-lines', {
    action: 'edit_adjustment',
    booking_id: bookingId,
    adjustment_id: adjustmentId,
    ...adjustmentPayload(input),
  }),

  /** Remove an adjustment outright. Same refusal as edit. */
  deleteAdjustment: async (bookingId, adjustmentId) => post('bookings/order-lines', {
    action: 'delete_adjustment',
    booking_id: bookingId,
    adjustment_id: adjustmentId,
  }),

  /**
   * Write the paired negative for a settled line: same amount, operator and
   * currency, linked to the original, with the reason the office typed.
   * Exactly one per line — a second is refused rather than doubling.
   */
  reverseAdjustment: async (bookingId, adjustmentId, reason) => post('bookings/order-lines', {
    action: 'reverse_adjustment',
    booking_id: bookingId,
    adjustment_id: adjustmentId,
    reason,
  }),
};
